<template>
  <v-dialog fullscreen v-model="dialog">
    <v-card>
      <v-toolbar dark color="primary" fixed style="z-index: 10000">
        <v-btn icon dark @click="close" id="system-log__cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{
          $t("modules.logs.systemLog.documentTitle")
        }}</v-toolbar-title>
      </v-toolbar>
      <v-overlay
        color="system_log__overlay"
        opacity="1.0"
        :z-index="9999"
        v-model="dialogLoading"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-overlay>
      <v-subheader>
        <v-chip color="primary" outlined
          >{{ $t("modules.logs.systemLog.deviceName") }}:
          {{ selected ? selected.device_details.device_name : "" }}
        </v-chip>
        <v-chip color="primary" outlined class="ml-2"
          >{{ $t("modules.logs.systemLog.serialNumber") }}:
          {{ selected ? selected.device_details.device_serial : "" }}
        </v-chip>
        <v-chip color="primary" outlined class="ml-2"
          >{{ $t("modules.logs.systemLog.deviceAttributeType") }}:
          {{
            selected
              ? util.parseDeviceAttributeType(selected.device_attribute_type)
              : ""
          }}
        </v-chip>
        <v-chip color="primary" outlined class="ml-2"
          >{{ $t("modules.logs.systemLog.endDate") }}:
          {{
            selected
              ? new Date(selected.log_time).toLocaleString($i18n.locale)
              : ""
          }}
        </v-chip>
      </v-subheader>
      <div class="pa-2">
        <system-log-graph
          :chart-data="chartData"
          :options="chartOptions"
          :styles="{ height: 500 + 'px' }"
        ></system-log-graph>
      </div>
      <v-card-actions>
        <v-btn
          color="primary"
          @click="previous"
          :loading="prevLoading"
          :disabled="prevLoading || nextLoading || previousQueryId.length === 0"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="next"
          :loading="nextLoading"
          :disabled="
            prevLoading ||
              nextLoading ||
              !nextQueryId ||
              deviceLogData.length < 50
          "
        >
          <v-icon>
            mdi-arrow-right
          </v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import util from "@/modules/logs/system-log/util";
import logsUtil from "@/modules/logs/util";
import SystemLogGraph from "./system-log-graph";
import { handleError } from "@/utils/error";

export default {
  name: "SystemLogDialog",
  components: { SystemLogGraph },
  data() {
    return {
      prevLoading: false,
      nextLoading: false,
      util,
      logsUtil,
      chartOptions: {
        elements: {
          line: {
            tension: 0
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              type: "time",
              display: true,
              scaleLabel: {
                display: false
              }
            }
          ]
        },
        legend: {
          display: false
        },
        tooltips: {
          position: "nearest",
          mode: "x",
          displayColors: false,
          callbacks: {
            title: (tooltipItem, data) => this.titleText(tooltipItem, data),
            label: (tooltipItem, data) => this.labelText(tooltipItem, data),
            afterLabel: (tooltipItem, data) =>
              this.afterLabelText(tooltipItem, data)
          }
        }
      },
      headers: [
        {
          text: this.$t("modules.logs.systemLog.logTime"),
          align: "left",
          value: "log_time"
        },
        {
          text: this.$t("modules.logs.systemLog.value"),
          align: "start",
          value: "value"
        },
        {
          text: this.$t("modules.logs.systemLog.severity"),
          align: "start",
          value: "severity"
        }
      ]
    };
  },
  computed: {
    dialog: {
      get() {
        return this.$store.state.systemLog.dialog;
      },
      set(val) {
        this.$store.commit("systemLog/updateDialog", val);
      }
    },
    selected: {
      get() {
        return this.$store.state.systemLog.selected;
      },
      set(val) {
        this.$store.commit("systemLog/updateSelected", val);
      }
    },
    dialogLoading: {
      get() {
        return this.$store.state.systemLog.dialogLoading;
      },
      set(val) {
        this.$store.commit("systemLog/updateDialogLoading", val);
      }
    },

    deviceLog() {
      return this.$store.state.systemLog.deviceLog;
    },

    deviceLogData() {
      return this.$store.getters["systemLog/deviceLogData"];
    },

    deviceLogTimestamp() {
      return this.$store.getters["systemLog/deviceLogTimestamp"];
    },

    chartData() {
      return {
        labels: this.deviceLogTimestamp,
        datasets: [
          {
            label: this.$t("layouts.app.systemLog"),
            backgroundColor: this.$vuetify.theme.themes.light
              .system_log__chart_background,
            borderColor: this.$vuetify.theme.themes.light
              .system_log__chart_border,
            data: this.deviceLogData
          }
        ]
      };
    },
    nextQueryId() {
      return this.$store.state.systemLog.nextQueryId;
    },
    previousQueryId() {
      return this.$store.state.systemLog.previousQueryId;
    }
  },

  methods: {
    titleText(tooltipItem, data) {
      return new Date(tooltipItem[0].xLabel).toLocaleString(this.$i18n.locale);
    },
    labelText(tooltipItem, data) {
      let label = this.$t("modules.logs.systemLog.value");
      label += ": ";
      label += tooltipItem.yLabel;
      return label;
    },

    afterLabelText(tooltipItem, data) {
      let label = this.$t("modules.logs.systemLog.severity");
      label += ": ";
      label += logsUtil.parseSeverity(
        data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].severity
      );
      return label;
    },

    close() {
      this.dialog = false;
    },

    next() {
      this.nextLoading = true;
      this.$store
        .dispatch("systemLog/getDeviceLog", {
          id: this.selected.device_id,
          attributeType: this.selected.device_attribute_type,
          object_id: this.nextQueryId,
          logTime: util.parseDate(this.selected.log_time)
        })
        .then(response => {
          let data = response.data.system_logs;
          if (data.length > 0) {
            let previousQueryId = [...this.previousQueryId, this.nextQueryId];
            let nextQueryId = data[data.length - 1].id;
            this.$store.commit("systemLog/updateQueryId", {
              nextQueryId,
              previousQueryId
            });
          } else {
            this.$store.commit("systemLog/updateQueryId", {
              nextQueryId: null,
              previousQueryId: [...this.previousQueryId, this.nextQueryId]
            });
          }
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.nextLoading = false;
        });
    },
    previous() {
      this.prevLoading = true;
      this.$store
        .dispatch("systemLog/getDeviceLog", {
          id: this.selected.device_id,
          attributeType: this.selected.device_attribute_type,
          object_id: this.previousQueryId[this.previousQueryId.length - 2],
          logTime: util.parseDate(this.selected.log_time)
        })
        .then(response => {
          let nextQueryId =
            response.data.system_logs[response.data.system_logs.length - 1].id;
          let data = this.previousQueryId.slice();
          data.pop(1);
          let previousQueryId = [...data];
          this.$store.commit("systemLog/updateQueryId", {
            nextQueryId,
            previousQueryId
          });
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.prevLoading = false;
        });
    }
  }
};
</script>

<style scoped></style>
